// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/qa-criteria-system.md

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  Error,
  Info
} from '@mui/icons-material';
import { apiClient, API_ENDPOINTS } from '../../config/api';

/**
 * QA Context Preview Step Component
 * 
 * Step 6 of QA Criteria Creation Wizard
 * Generates and displays AI-powered context preview
 */
const QAContextPreviewStep = ({ formData, errors, onChange }) => {
  const [preview, setPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [lastGenerated, setLastGenerated] = useState(null);

  // Auto-generate preview when form data changes
  useEffect(() => {
    if (formData.qa_context && hasRequiredContext()) {
      generatePreview();
    }
  }, [formData.qa_context, formData.categories]);

  const hasRequiredContext = () => {
    const context = formData.qa_context;
    return context?.conversation_type && 
           context?.participants && 
           context?.target_audience &&
           context?.conversation_objective &&
           context?.ideal_approach;
  };

  const generatePreview = async () => {
    if (!hasRequiredContext()) {
      setError('Please complete all required context fields first');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const response = await apiClient.post(API_ENDPOINTS.QA_CONTEXT_GENERATE_PREVIEW, {
        conversation_type: formData.qa_context.conversation_type,
        participants: formData.qa_context.participants,
        conversation_objective: formData.qa_context.conversation_objective,
        ideal_approach: formData.qa_context.ideal_approach,
        context_notes: formData.qa_context.context_notes,
        target_audience: formData.qa_context.target_audience,
        categories: formData.categories,
        criteria_type: formData.criteria_type || 'matrix'
      });

      if (response.data.success) {
        setPreview(response.data.preview);
        setLastGenerated(new Date());
        
        // Store the generated preview in form data
        onChange('qa_context_preview', {
          preview: response.data.preview,
          generated_at: new Date().toISOString(),
          processing_time: response.data.processing_time,
          model_used: response.data.model_used
        });
      } else {
        setError('Failed to generate preview');
      }
    } catch (err) {
      console.error('Error generating context preview:', err);
      setError(err.response?.data?.detail || 'Failed to generate context preview');
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = () => {
    generatePreview();
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        QA Context Preview
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Review the AI-generated context preview that will be used for voice analysis. 
        This preview helps ensure the AI understands the conversation context correctly.
      </Typography>

      {errors.qaContextPreview && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.qaContextPreview}
        </Alert>
      )}

      {/* Context Summary */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>
          Context Summary
        </Typography>
        <Grid container spacing={1}>
          {formData.qa_context?.conversation_type && (
            <Grid item>
              <Chip 
                label={`Type: ${formData.qa_context.conversation_type}`} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            </Grid>
          )}
          {formData.qa_context?.participants && (
            <Grid item>
              <Chip 
                label={`Participants: ${formData.qa_context.participants}`} 
                size="small" 
                color="secondary" 
                variant="outlined"
              />
            </Grid>
          )}
          {formData.qa_context?.target_audience && (
            <Grid item>
              <Chip 
                label={`Audience: ${formData.qa_context.target_audience}`} 
                size="small" 
                color="success" 
                variant="outlined"
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Generate/Regenerate Button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          {lastGenerated && `Last generated: ${lastGenerated.toLocaleTimeString()}`}
        </Typography>
        <Button
          variant="outlined"
          startIcon={generating ? <CircularProgress size={16} /> : <Refresh />}
          onClick={handleRegenerate}
          disabled={generating || !hasRequiredContext()}
        >
          {generating ? 'Generating...' : 'Regenerate Preview'}
        </Button>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Preview Content */}
      {generating ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Generating context preview...
          </Typography>
        </Box>
      ) : preview ? (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <CheckCircle color="success" sx={{ mr: 1 }} />
            <Typography variant="subtitle1">
              Generated Context Preview
            </Typography>
            {formData.qa_context_preview?.processing_time && (
              <Chip 
                label={`${formData.qa_context_preview.processing_time.toFixed(2)}s`}
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          <Typography 
            variant="body2" 
            component="pre" 
            sx={{ 
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              lineHeight: 1.6,
              bgcolor: 'grey.50',
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}
          >
            {preview}
          </Typography>
        </Paper>
      ) : (
        <Alert severity="info" icon={<Info />}>
          {hasRequiredContext() 
            ? 'Click "Regenerate Preview" to generate the context preview'
            : 'Please complete all required context fields to generate preview'
          }
        </Alert>
      )}

      {/* Validation Status */}
      {preview && (
        <Box mt={2}>
          <Alert severity="success" icon={<CheckCircle />}>
            Context preview generated successfully. You can proceed to the next step.
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default QAContextPreviewStep;
